import Select from '@storeblocks/select';
import { useField, useFormikContext } from 'formik';
import { type FC } from 'react';
import { styled } from 'styled-components';

import { OrderType } from '@/api/order/models/OrderType';
import { useTexts } from '@/hooks/useTexts';
import { nameof } from '@/util/nameof';

import { useFundPortfolios } from '../../../hooks/useFundPortfolios';
import { RedemptionPayment } from '../../../types/Payment';
import { RedemptionOrderFormValues } from '../../../types/RedemptionOrderFormValues';
import { getPortfolioBankAccounts } from '../../../utils/getPortfolioBankAccounts';

interface Props {
  paymentIndex: number;
}

export const RedemptionPortfolioBankAccount: FC<Props> = ({ paymentIndex }) => {
  const texts = useTexts();
  const formContext = useFormikContext<RedemptionOrderFormValues>();
  const portfolios = useFundPortfolios(OrderType.Redemption);

  const [field, meta] = useField<string>(
    `payments[${paymentIndex}].portfolioBankAccountNumber`,
  );

  const [payments] = useField<RedemptionPayment[]>(
    nameof<RedemptionOrderFormValues>('payments'),
  );
  const payment = payments.value[paymentIndex];

  const portfolioBankAccounts = getPortfolioBankAccounts(
    formContext.values.fromPortfolioShortName,
    portfolios,
  );

  const hasMultiplePortfolioBankAccounts = portfolioBankAccounts.length > 1;

  if (hasMultiplePortfolioBankAccounts) {
    // When there are multiple portfolio bank accounts, we show a select input
    // to let the user choose the account where the payment will be placed.

    const options = portfolioBankAccounts.map((account) => ({
      label: `${account.accountNumber} - ${account.bankName} - ${account.currency}`,
      value: account.accountNumber,
    }));

    return (
      <Select
        {...field}
        id={`portfolioBankAccount-select-${payment.currency}`}
        label={texts.orders.form.payment.options.portfolioSelect.title}
        placeholder={
          texts.orders.form.payment.options.portfolioSelect.placeholder
        }
        options={options}
        alertMessageType={meta.touched && meta.error ? 'error' : undefined}
        alertMessage={meta.error}
        fluid
      />
    );
  }

  // When there is only one portfolio bank account, we show the account
  // details for that account.

  const account = portfolioBankAccounts[0];

  return (
    <div data-test="one-portfolio-account">
      <PaymentTitle>
        {texts.orders.form.payment.redemption.payoutToPortfolioAccount}
      </PaymentTitle>

      <PaymentSubtitle>
        {`${account.accountNumber} - ${account.bankName} - ${account.currency}`}
      </PaymentSubtitle>
    </div>
  );
};

const PaymentTitle = styled.div`
  font-weight: 600;
  line-height: 24px;
  word-wrap: break-word;
  margin-bottom: 16px;
`;

const PaymentSubtitle = styled.div`
  line-height: 24px;
  word-wrap: break-word;
`;
